/**
 * Event status colours carried over from Fleet Track 1.0.
 *
 * FT1 stores a status code's colours on its legacy `StatusCode` row as either hex
 * (`#RRGGBB`, sometimes without the `#`) or a colour name. The names include FT1's own palette
 * and LBT's pastel set (OpenGTS `ColorTools.java`), which browsers do not know.
 */

// FT1's named colours, with FT1's values (they differ from the CSS colours of the same name).
export const FT1_NAMED_COLORS: Record<string, string> = {
  black: "#000000",
  brown: "#A52A2A",
  red: "#DD0000",
  orange: "#FFA500",
  yellow: "#FFD700",
  darkyellow: "#AA9700",
  green: "#00CC00",
  lbtgreen: "#C7F9C7",
  lbtred: "#F9C7C7",
  lbtyellow: "#FDFD96",
  lbtgray: "#EFEFEF",
  lbtblue: "#DBD9F7",
  blue: "#0000EE",
  purple: "#9400D3",
  gray: "#808080",
  darkgray: "#505050",
  lightgray: "#C0C0C0",
  white: "#FFFFFF",
  cyan: "#00FFFF",
  pink: "#FF1493",
  magenta: "#8B008B",
};

// Swatches offered on the event-colours page: FT1's dropdown, then the Okabe-Ito palette,
// which stays distinguishable for the common forms of colour blindness.
export const FT1_DROPDOWN_COLORS: { label: string; value: string }[] = [
  { label: "Black", value: "#000000" },
  { label: "Brown", value: "#A52A2A" },
  { label: "Red", value: "#DD0000" },
  { label: "Orange", value: "#FFA500" },
  { label: "Yellow", value: "#FFD700" },
  { label: "Green", value: "#00CC00" },
  { label: "Blue", value: "#0000EE" },
  { label: "Purple", value: "#9400D3" },
  { label: "Gray", value: "#808080" },
  { label: "White", value: "#FFFFFF" },
  { label: "LBT Green", value: "#C7F9C7" },
  { label: "LBT Red", value: "#F9C7C7" },
  { label: "LBT Yellow", value: "#FDFD96" },
  { label: "LBT Gray", value: "#EFEFEF" },
  { label: "LBT Blue", value: "#DBD9F7" },
];

export const COLORBLIND_SAFE_COLORS: { label: string; value: string }[] = [
  { label: "Orange", value: "#E69F00" },
  { label: "Sky blue", value: "#56B4E9" },
  { label: "Bluish green", value: "#009E73" },
  { label: "Yellow", value: "#F0E442" },
  { label: "Blue", value: "#0072B2" },
  { label: "Vermillion", value: "#D55E00" },
  { label: "Reddish purple", value: "#CC79A7" },
];

export const MIN_STATUS_CONTRAST = 3;

const HEX_RE = /^#?([0-9a-f]{6}|[0-9a-f]{3})$/i;
const CSS_NAME_RE = /^[a-z]+$/i;
export const STRICT_HEX_RE = /^#[0-9a-f]{6}$/i;

const cssSupportsColor = (value: string): boolean => {
  if (typeof CSS !== "undefined" && typeof CSS.supports === "function") {
    return CSS.supports("color", value);
  }
  return true; // no CSS object (tests, old browsers): trust the name check alone
};

/**
 * A stored FT1 colour as CSS: hex becomes `#RRGGBB`, FT1/LBT names become their hex, other
 * plain CSS colour names pass through. Empty or unreadable values give `undefined`.
 */
export const toCssColor = (value?: string | null): string | undefined => {
  if (value === null || value === undefined) return undefined;
  const text = `${value}`.trim();
  if (!text) return undefined;
  const named = FT1_NAMED_COLORS[text.toLowerCase().replace(/[\s_]/g, "")];
  if (named) return named;
  const hex = HEX_RE.exec(text);
  if (hex) {
    const digits =
      hex[1].length === 3
        ? hex[1]
            .split("")
            .map((ch) => ch + ch)
            .join("")
        : hex[1];
    return `#${digits.toUpperCase()}`;
  }
  if (CSS_NAME_RE.test(text) && cssSupportsColor(text)) return text.toLowerCase();
  return undefined;
};

export type StatusWithColor = {
  label: string;
  fg?: string;
  bg?: string;
};

/** Split a report's `desc|fg|bg` value; colours come back as CSS or undefined. */
export const parseStatusWithColor = (value: unknown): StatusWithColor => {
  if (value === null || value === undefined) return { label: "" };
  const [label = "", fg, bg] = `${value}`.split("|");
  return { label, fg: toCssColor(fg), bg: toCssColor(bg) };
};

const channel = (c: number) => {
  const s = c / 255;
  return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
};

/** WCAG relative luminance of a `#RRGGBB` colour, or undefined for anything else. */
export const relativeLuminance = (hex?: string): number | undefined => {
  const css = toCssColor(hex);
  if (!css || !STRICT_HEX_RE.test(css)) return undefined;
  const r = parseInt(css.slice(1, 3), 16);
  const g = parseInt(css.slice(3, 5), 16);
  const b = parseInt(css.slice(5, 7), 16);
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
};

/** WCAG contrast ratio between two colours (1 to 21), or undefined if either is not hex. */
export const contrastRatio = (a?: string, b?: string): number | undefined => {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  if (la === undefined || lb === undefined) return undefined;
  const [hi, lo] = la > lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
};

/** Black or white, whichever reads better on `background`. */
export const readableTextOn = (background?: string): string => {
  const onBlack = contrastRatio("#000000", background);
  const onWhite = contrastRatio("#FFFFFF", background);
  if (onBlack === undefined || onWhite === undefined) return "#000000";
  return onBlack >= onWhite ? "#000000" : "#FFFFFF";
};
